import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "¿Mis clientes darán su tarjeta?",
    a: "En República Dominicana ya es habitual pagar suscripciones (Netflix, Spotify, apps). El checkout lleva tu marca y explicamos que la tarjeta se guarda de forma segura para cobrar sin volver a pedírsela. La conversión mejora frente a transferencias manuales.",
  },
  {
    q: "¿Y si falla un pago?",
    a: "El sistema reintenta automáticamente (día 0, 3 y 7) y le avisa al cliente por email. Si no se recupera al día 14, se suspende el servicio. Tú no haces nada, recuperas ingresos en piloto automático.",
  },
  {
    q: "¿Cómo funciona la comisión?",
    a: "Pagas USD 54.99/mes fijo + 2% de comisión solo sobre pagos online procesados por Kubo Payments. La comisión del adquirente (CardNet, Azul, Portal o Mio) es aparte según tu contrato con ellos. No hay comisión sobre efectivo, transferencias ni POS presencial.",
  },
  {
    q: "¿Qué pasa con la facturación electrónica?",
    a: "La facturación electrónica está integrada al flujo: cada transacción genera su comprobante automáticamente y queda trazable para contabilidad. Eso te ahorra trabajo manual, reduce errores y te da tranquilidad operativa sin procesos paralelos.",
  },
  {
    q: "¿Cuánto toma empezar?",
    a: "La afiliación al adquirente depende de sus tiempos, pero nosotros gestionamos el proceso sin costo añadido. Una vez afiliado, conectarlo toma 1 minuto. Después, configurar tus planes y empezar a cobrar es rápido: en la demo te dejamos el paso a paso claro para tu caso.",
  },
  {
    q: "¿Necesito un programador o integraciones?",
    a: "No. Kubo está diseñado para implementarse sin código. Configuras tus planes, tu checkout con tu marca y listo. Lo técnico pesado no debe ser tu carga: tú ves resultados, no \"proyectos\".",
  },
  {
    q: "¿Puedo importar mis datos (clientes, productos o transacciones)?",
    a: "Sí. Puedes importar información para arrancar con tu operación ordenada desde el día uno, sin tener que volver a crear todo a mano. Eso acelera la implementación y te permite ver tu realidad en el sistema desde el inicio.",
  },
  {
    q: "¿Qué control tiene mi cliente sobre cobros, pausas o cancelaciones?",
    a: "Tu cliente puede autogestionarse desde su portal: actualizar su método de pago, pausar o cancelar según lo que tú permitas. Eso reduce tickets, evita conversaciones incómodas y mejora la experiencia del cliente final (sin que tu equipo cargue con todo).",
  },
];

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(-1);

  const leftFaqs = faqs.slice(0, 4);
  const rightFaqs = faqs.slice(4);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-8 md:py-12 bg-[#F2EDE3]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          className="max-w-3xl mx-auto text-center mb-16 md:mb-20"
        >
          <span className="text-base font-bold uppercase tracking-[0.2em] text-[#111111]">
            Preguntas frecuentes
          </span>
          <h2 className="mt-4 font-['Cabinet_Grotesk'] font-black tracking-tighter leading-[0.95] text-[#075a22] text-4xl sm:text-5xl md:text-6xl">
            Lo que todos preguntan.
          </h2>
          <p className="mt-5 text-lg text-[#111111]/85 leading-relaxed">
            Si tienes otra duda, escríbenos. Contestamos rápido y sin bots.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* Left column */}
          <div className="space-y-4">
            {leftFaqs.map((f, i) => {
              const isOpen = openIdx === i;
              return (
                <div key={f.q} data-testid={`faq-item-${i}`} className="h-auto">
                  <button
                    onClick={() => setOpenIdx(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-6 py-5 px-4 text-left group bg-white border border-[#111111]/10 rounded-2xl hover:border-[#a68a6d]/30 transition-all"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span
                      className={`font-['Cabinet_Grotesk'] text-lg md:text-xl font-bold tracking-tight transition-colors ${
                        isOpen ? "text-[#a68a6d]" : "text-[#111111]"
                      } group-hover:text-[#a68a6d]`}
                    >
                      {f.q}
                    </span>
                    <div
                      className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        isOpen
                          ? "bg-[#a68a6d] text-white"
                          : "bg-[#111111]/5 text-[#111111]"
                      }`}
                    >
                      {isOpen ? (
                        <svg className="w-4 h-4" strokeWidth={2.5} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" strokeWidth={2.5} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden px-4"
                      >
                        <p className="pb-6 text-base leading-relaxed text-[#111111]/85">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {rightFaqs.map((f, i) => {
              const globalIdx = i + 4;
              const isOpen = openIdx === globalIdx;
              return (
                <div key={f.q} data-testid={`faq-item-${globalIdx}`} className="h-auto">
                  <button
                    onClick={() => setOpenIdx(isOpen ? -1 : globalIdx)}
                    className="w-full flex items-center justify-between gap-6 py-5 px-4 text-left group bg-white border border-[#111111]/10 rounded-2xl hover:border-[#a68a6d]/30 transition-all"
                    data-testid={`faq-toggle-${globalIdx}`}
                  >
                    <span
                      className={`font-['Cabinet_Grotesk'] text-lg md:text-xl font-bold tracking-tight transition-colors ${
                        isOpen ? "text-[#a68a6d]" : "text-[#111111]"
                      } group-hover:text-[#a68a6d]`}
                    >
                      {f.q}
                    </span>
                    <div
                      className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        isOpen
                          ? "bg-[#a68a6d] text-white"
                          : "bg-[#111111]/5 text-[#111111]"
                      }`}
                    >
                      {isOpen ? (
                        <svg className="w-4 h-4" strokeWidth={2.5} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" strokeWidth={2.5} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden px-4"
                      >
                        <p className="pb-6 text-base leading-relaxed text-[#111111]/85">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cierre suave */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          className="mt-16 text-center text-lg text-[#111111]/85 max-w-2xl mx-auto"
        >
          Si esto describe tu negocio, agenda una demo y te mostramos el flujo
          completo con tu vertical (gimnasio, academia deportiva o colegio).
        </motion.p>
      </div>
    </section>
  );
}
